import { CommentCancelButton } from '@/components/element/Comments/CommentCancelButton';
import { CommentContent } from '@/components/element/Comments/CommentContent';
import { CommentMenu } from '@/components/element/Comments/CommentMenu';
import { CommentReplayButton } from '@/components/element/Comments/CommentReplayButton';
import { DraftEditor } from '@/components/element/Form/draft-js/DraftEditor';
import { contentStateToHtml } from '@/components/element/Form/draft-js/DraftJsonText';
import { MotionDiv } from '@/components/element/MotionDiv';
import { useCreateCommentAttachment } from '@/hooks/api/2151_create_comment_attachment';
import {
  InvoiceTaskComment,
  PaymentTaskComment,
  PurchaseOrderComment,
  PurchaseOrderContractTaskComment,
  QuotationTaskComment,
  Reaction,
  SalesOrderComment,
  SalesOrderContractTaskComment,
  User,
} from '@/hooks/api/models';
import { fileToBase64 } from '@/hooks/useBase64FromFileInput';
import { UPLOADABLE_MIME_TYPES } from '@/lib/constants';
import { storage } from '@/lib/storage';
import { toastApiError, toastError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { dateTimeFormat } from '@/utils';
import '@draft-js-plugins/anchor/lib/plugin.css';
import { createEditorStateWithText } from '@draft-js-plugins/editor';
import '@draft-js-plugins/inline-toolbar/lib/plugin.css';
import { MentionData } from '@draft-js-plugins/mention';
import '@draft-js-plugins/mention/lib/plugin.css';
import { AtomicBlockUtils, convertFromRaw, convertToRaw, EditorState, Modifier, SelectionState } from 'draft-js';
import EmojiPicker, { Theme } from 'emoji-picker-react';
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

const FILE_SIZE_LIMIT = 1024 * 1024 * 50;

type MemberType = User;
type CommentType =
  | PurchaseOrderComment
  | PurchaseOrderContractTaskComment
  | SalesOrderComment
  | SalesOrderContractTaskComment
  | QuotationTaskComment
  | InvoiceTaskComment
  | PaymentTaskComment;
type CommentListType =
  | PurchaseOrderComment[]
  | PurchaseOrderContractTaskComment[]
  | SalesOrderComment[]
  | SalesOrderContractTaskComment[]
  | QuotationTaskComment[]
  | InvoiceTaskComment[]
  | PaymentTaskComment[];

export type OnCreateCommentProps = {
  content_json: string;
  content: string;
  setComment: Dispatch<SetStateAction<CommentType | undefined>>;
  setError: Dispatch<SetStateAction<Error | null>>;
  parent_id?: number;
};

export type OnUpdateCommentProps = {
  id: number;
  content_json: string;
  content: string;
  setComment: Dispatch<SetStateAction<CommentType | undefined>>;
  setError: Dispatch<SetStateAction<Error | null>>;
};

export type OnDeleteCommentProps = {
  id: number;
  setComment: Dispatch<SetStateAction<CommentType | undefined>>;
  setError: Dispatch<SetStateAction<Error | null>>;
};

export type OnToggleReactionProps = {
  comment_id: number;
  emoji_id: string;
  content: string;
  setComment: Dispatch<SetStateAction<CommentType | undefined>>;
  setError: Dispatch<SetStateAction<Error | null>>;
};

type CommentsProps = {
  identifier: string;
  comments: CommentListType | undefined;
  user: MemberType;
  mentions: MentionData[];
  onCreateComment: ({ content, content_json, setComment, setError, parent_id }: OnCreateCommentProps) => void;
  onToggleReaction: ({ content, setComment, setError }: OnToggleReactionProps) => void;
  onUpdateComment?: ({ ...props }: OnUpdateCommentProps) => void;
  onDeleteComment?: ({ ...props }: OnDeleteCommentProps) => void;
};

export const Comments = ({
  identifier,
  comments,
  user,
  mentions,
  onCreateComment,
  onToggleReaction,
  onUpdateComment,
  onDeleteComment,
}: CommentsProps) => {
  const { setShowLoading } = useLoading();
  const [activeReactionId, setActiveReactionId] = useState<number | null>(null);
  const [editorState, setEditorState] = useState(() => createEditorStateWithText(''));

  const [comment, setComment] = useState<CommentType>();
  const [error, setError] = useState<Error | null>(null);

  const onSendClick = () => {
    setShowLoading(true);
    const content = contentStateToHtml(editorState.getCurrentContent());
    const content_json = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
    onCreateComment({ content_json, content: content!, setComment, setError });
  };

  useEffect(() => {
    if (comment) {
      comments!.push(comment);
      setEditorState(EditorState.createEmpty());
      setShowLoading(false);
    }
  }, [comment]);

  useEffect(() => {
    setShowLoading(false);
    toastApiError(error);
  }, [error]);

  useEffect(() => {
    const handleClick = (e: any) => {
      if (e.target.classList.contains('reaction-btn')) {
        return;
      }

      const a = document.querySelector('.EmojiPickerReact');
      if (a && !a.contains(e.target)) {
        setActiveReactionId(null);
      }
    };

    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  const [dataRestored, setDataRestored] = useState(false);
  useEffect(() => {
    const contentJson = storage.comment.get(identifier);
    if (contentJson) {
      setEditorState(EditorState.createWithContent(convertFromRaw(contentJson)));
    }
    setDataRestored(true);
  }, []);

  useEffect(() => {
    if (dataRestored) {
      storage.comment.set(identifier, convertToRaw(editorState.getCurrentContent()));
    }
  }, [dataRestored, editorState]);

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header">
        <h6 className="mb-0">コメント</h6>
      </div>
      <div className="card-body pt-0">
        <div className="mb-1">
          {comments?.map((d) => (
            <CommentRow
              key={`c-${d.id}`}
              d={d}
              user={user}
              mentions={mentions}
              onCreateComment={onCreateComment}
              onToggleReaction={onToggleReaction}
              activeReactionId={activeReactionId}
              setActiveReactionId={setActiveReactionId}
              onUpdateComment={onUpdateComment}
              onDeleteComment={onDeleteComment}
            />
          ))}

          <div className="d-flex">
            <img className="avatar rounded-circle me-3 flex-shrink-0" src={user.user_account.icon_file_url} />
            <CommentEditorElement
              mentions={mentions}
              onSendClick={onSendClick}
              editorState={editorState}
              setEditorState={setEditorState}
            />
          </div>
        </div>
      </div>
    </MotionDiv>
  );
};

type CommentRowProps = {
  d: CommentType;
  user: MemberType;
  mentions: MentionData[];
  onCreateComment: ({ content, content_json, setComment, setError, parent_id }: OnCreateCommentProps) => void;
  onToggleReaction: ({ content, setComment, setError }: OnToggleReactionProps) => void;
  activeReactionId: number | null;
  setActiveReactionId: Dispatch<SetStateAction<number | null>>;
  onUpdateComment?: ({ id, content, content_json, setComment, setError }: OnUpdateCommentProps) => void;
  onDeleteComment?: ({ id, setComment, setError }: OnDeleteCommentProps) => void;
};

const CommentRow = ({
  d,
  mentions,
  user,
  onCreateComment,
  onToggleReaction,
  activeReactionId,
  setActiveReactionId,
  onUpdateComment,
  onDeleteComment,
}: CommentRowProps) => {
  const { setShowLoading } = useLoading();
  const [reply, setReply] = useState<boolean>(false);
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());

  const [comment, setComment] = useState<CommentType>();
  const [reactionComment, setReactionComment] = useState<CommentType>();
  const [error, setError] = useState<Error | null>(null);
  const [editing, setEditing] = useState<CommentType | null>(null);

  const onToggleReactionClick = (comment_id: number, emoji_id: string, content: string) => {
    setActiveReactionId(null);
    setShowLoading(true);
    onToggleReaction({ comment_id, emoji_id, content, setComment: setReactionComment, setError });
  };

  const onSendClick = () => {
    setShowLoading(true);
    const content = contentStateToHtml(editorState.getCurrentContent());
    const content_json = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
    onCreateComment({ content_json, content, setComment, setError, parent_id: d.id });
  };

  const onUpdateClick = (comment: CommentType) => {
    setShowLoading(true);
    const content = contentStateToHtml(editorState.getCurrentContent());
    const content_json = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
    if (onUpdateComment) onUpdateComment({ content_json, content, setComment, setError, id: comment.id });
  };

  const onDeleteClick = (comment: CommentType) => {
    setShowLoading(true);
    if (onDeleteComment) onDeleteComment({ setComment, setError, id: comment.id });
  };

  // 編集ボタンクリック
  // 返信は取り消す
  useEffect(() => {
    if (editing) {
      setReply((_) => false);
      setEditorState(EditorState.createWithContent(convertFromRaw(editing.content_json)));
    }
  }, [editing]);

  // 返信ボタンクリック
  // 編集は取り消す
  useEffect(() => {
    if (reply) {
      setEditing((_) => null);
      setEditorState(EditorState.createEmpty());
    }
  }, [reply]);

  useEffect(() => {
    if (comment) {
      // 元コメントの編集の場合
      if (d.id == comment.id) {
        d.content = comment.content;
        d.content_json = comment.content_json;
      } else {
        const c = d.children.find((child) => child.id == comment.id);
        if (c) {
          // 返信の編集の場合
          c.content = comment.content;
          c.content_json = comment.content_json;
        } else {
          // 返信の場合
          d.children.push(comment);
        }
      }

      setEditorState(EditorState.createEmpty());
      setShowLoading(false);
      setReply(false);
      setEditing(null);
    }
  }, [comment]);

  useEffect(() => {
    if (reactionComment) {
      let _comment;
      if (d.id == reactionComment.id) {
        _comment = d;
      } else {
        _comment = d.children.find((c) => c.id == reactionComment.id);
      }

      if (_comment) {
        _comment.reactions = reactionComment.reactions;
      }

      setShowLoading(false);
    }
  }, [reactionComment]);

  useEffect(() => {
    toastApiError(error);
    setShowLoading(false);
  }, [error]);

  return (
    <div className="d-flex mb-4">
      <img className="avatar-sm rounded-circle flex-shrink-0" src={d.user.user_account.icon_file_url} />
      <div className="flex-grow-1 ms-3">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h6 className="mb-0">{d.user.user_account.name}</h6>
          <div className="d-flex align-items-center">
            <div className="text-muted text-xs">{dateTimeFormat({ date: d.created_at })}</div>
            <CommentMenu
              user={user}
              comment={d}
              onEditClick={() => setEditing(d)}
              onDeleteClick={() => onDeleteClick(d)}
            />
          </div>
        </div>
        {editing?.id == d.id ? (
          <>
            <div className="d-flex mt-2">
              <CommentCancelButton onClick={() => setEditing(null)} />
            </div>
            <div className="d-flex mt-2">
              <CommentEditorElement
                mentions={mentions}
                onSendClick={() => onUpdateClick(d)}
                editorState={editorState}
                setEditorState={setEditorState}
              />
            </div>
          </>
        ) : (
          <div className="comment-article">
            <CommentContent comment={d} />
          </div>
        )}
        <div className="emotes">
          {d.reactions.map((r, i) => (
            <ReactionButton
              key={`reaction-${d.id}-${i}`}
              user={user}
              comment_id={d.id}
              reaction={r}
              onToggleReactionClick={onToggleReactionClick}
            />
          ))}
          <ReactionPickerOpenButton comment_id={d.id} setActiveReactionId={setActiveReactionId} />
        </div>

        <div className={'slack-selector-wrapper'} style={{ position: 'relative' }}>
          {activeReactionId == d.id && (
            <ReactionPicker comment_id={d.id} onToggleReactionClick={onToggleReactionClick} />
          )}
        </div>

        {d.children.map((child) => (
          <div key={`c-${d.id}-${child.id}`} className="d-flex mt-2">
            <img className="avatar-sm rounded-circle flex-shrink-0" src={child.user.user_account.icon_file_url} />
            <div className="flex-grow-1 ms-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <h6 className="mb-0">{child.user.user_account.name}</h6>
                <div className="d-flex align-items-center">
                  <div className="text-muted text-xs">{dateTimeFormat({ date: child.created_at })}</div>
                  <CommentMenu
                    user={user}
                    comment={child}
                    onEditClick={() => setEditing(child)}
                    onDeleteClick={() => onDeleteClick(child)}
                  />
                </div>
              </div>
              {editing?.id == child.id ? (
                <>
                  <div className="d-flex mt-2">
                    <CommentCancelButton onClick={() => setEditing(null)} />
                  </div>
                  <div className="d-flex mt-2">
                    <CommentEditorElement
                      mentions={mentions}
                      onSendClick={() => onUpdateClick(child)}
                      editorState={editorState}
                      setEditorState={setEditorState}
                    />
                  </div>
                </>
              ) : (
                <div className="comment-article">
                  <CommentContent comment={child} />
                </div>
              )}
              <div className="emotes">
                {child.reactions.map((r, i) => (
                  <ReactionButton
                    key={`reaction-${child.id}-${i}`}
                    user={user}
                    comment_id={child.id}
                    reaction={r}
                    onToggleReactionClick={onToggleReactionClick}
                  />
                ))}
                <ReactionPickerOpenButton comment_id={child.id} setActiveReactionId={setActiveReactionId} />
              </div>

              <div className={'slack-selector-wrapper'} style={{ position: 'relative' }}>
                {activeReactionId == child.id && (
                  <ReactionPicker comment_id={child.id} onToggleReactionClick={onToggleReactionClick} />
                )}
              </div>
            </div>
          </div>
        ))}

        <div className="d-flex">
          {reply && <CommentCancelButton onClick={() => setReply(false)} />}
          {!reply && <CommentReplayButton onClick={() => setReply(true)} />}
        </div>

        {reply && (
          <div className="d-flex mt-4">
            <img className="avatar rounded-circle me-3 flex-shrink-0" src={user.user_account.icon_file_url} />
            <CommentEditorElement
              mentions={mentions}
              onSendClick={onSendClick}
              editorState={editorState}
              setEditorState={setEditorState}
            />
          </div>
        )}
      </div>
    </div>
  );
};

type ReactionPickerOpenButtonProps = {
  comment_id: number;
  setActiveReactionId: Dispatch<SetStateAction<number | null>>;
};

const ReactionPickerOpenButton = ({ comment_id, setActiveReactionId }: ReactionPickerOpenButtonProps) => {
  return (
    <button className="launcher reaction-btn" onClick={() => setActiveReactionId(comment_id)}>
      <i className="far fa-smile reaction-btn"></i>
      <span className="reaction-btn">
        <i className="fas fa-plus reaction-btn"></i>
      </span>
    </button>
  );
};

type ReactionPickerProps = {
  comment_id: number;
  onToggleReactionClick: (comment_id: number, emoji_id: string, content: string) => void;
};

const ReactionPicker = ({ comment_id, onToggleReactionClick }: ReactionPickerProps) => {
  return (
    <EmojiPicker
      skinTonesDisabled={true}
      onEmojiClick={(emojiData) => {
        onToggleReactionClick(comment_id, emojiData.unified, emojiData.emoji);
      }}
      theme={Theme.LIGHT}
    />
  );
};

type ReactionButtonProps = {
  user: MemberType;
  comment_id: number;
  reaction: Reaction;
  onToggleReactionClick: (comment_id: number, emoji_id: string, content: string) => void;
};

const ReactionButton = ({ user, comment_id, reaction, onToggleReactionClick }: ReactionButtonProps) => {
  return (
    <button
      className={`emote ${reaction.users.some((u) => u.id == user.id) && 'active'}`}
      data-users={reaction.users.map((u) => u.user_account.name).join(',')}
      onClick={() => onToggleReactionClick(comment_id, reaction.emoji_id, reaction.content)}
    >
      {reaction.content}
      <span>{reaction.users.length}</span>
    </button>
  );
};

type CommentEditorElementProps = {
  mentions: MentionData[];
  onSendClick: () => void;
  editorState: EditorState;
  setEditorState: Dispatch<SetStateAction<EditorState>>;
};
const CommentEditorElement = ({ mentions, onSendClick, editorState, setEditorState }: CommentEditorElementProps) => {
  const { setShowLoading } = useLoading();
  const { _w_ } = useCurrentWorkspace();
  const [sendable, setSendable] = useState<boolean>();
  const [base64Content, setBase64Content] = useState<string>();
  const [selectedFile, setSelectedFile] = useState<File>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // const { mutate, data, isSuccess, isError } = useCreateCommentImage();
  const { mutate, data, isSuccess, isError, error } = useCreateCommentAttachment();

  useEffect(() => {
    // setSendable(editorState.getCurrentContent().getPlainText().trim().length > 0);
    // const contentBlocks = convertToRaw(editorState.getCurrentContent()).blocks;
    // const isEmpty = contentBlocks.every((d) => d.text.trim().length == 0 && d.type == 'unstyled');
    setSendable(!isEditorStateEmpty(editorState));
  }, [editorState]);

  useEffect(() => {
    if (base64Content) {
      setShowLoading(true);
      mutate({
        identifier: _w_!.identifier,
        file: base64Content,
        file_name: selectedFile!.name,
        mime_type: selectedFile!.type,
      });
    }
  }, [base64Content]);

  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (data) {
      if (['image/gif', 'image/png', 'image/jpeg'].includes(data.mime_type)) {
        appendImage(data.url);
      } else {
        appendAttachment(data.file_name, data.url);
      }
    }
  }, [data]);

  useEffect(() => {
    if (isSuccess || isError) setShowLoading(false);
  }, [isSuccess, isError]);

  const appendImage = (url: string) => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntry = contentState.createEntity('image', 'IMMUTABLE', { src: url });
    const entityKey = contentStateWithEntry.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntry });
    const _newEditorState = AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ');
    setEditorState(_newEditorState);
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.files && e.currentTarget.files[0]) {
      const file = e.currentTarget.files[0];
      onFileSelect(file);
    }
  };

  const appendAttachment = (name: string, url: string) => {
    const contentState = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const textWithSpace = `${name} \n`;
    const newContent = Modifier.insertText(contentState, selection, textWithSpace);
    const newContentWithEntity = newContent.createEntity('LINK', 'MUTABLE', {
      download: 'test.png',
      target: '_blank',
      url: url,
    });
    const entityKey = newContentWithEntity.getLastCreatedEntityKey();
    const anchorOffset = selection.getAnchorOffset();
    const newSelection = new SelectionState({
      anchorKey: selection.getAnchorKey(),
      anchorOffset,
      focusKey: selection.getAnchorKey(),
      focusOffset: anchorOffset + name.length,
    });
    const newContentWithLink = Modifier.applyEntity(newContentWithEntity, newSelection, entityKey);
    const withLinkText = EditorState.push(editorState, newContentWithLink, 'insert-characters');
    const withProperCursor = EditorState.forceSelection(withLinkText, newContent.getSelectionAfter());
    setEditorState(withProperCursor);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      onFileSelect(file);
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (e.clipboardData.files && e.clipboardData.files.length > 0) {
      const file = e.clipboardData.files[0];
      onFileSelect(file);
    }
  };

  const onFileSelect = (file: File) => {
    if (file.size > FILE_SIZE_LIMIT) {
      toastError('50MBを超えるファイルは添付できません');
    } else if (!UPLOADABLE_MIME_TYPES.includes(file.type)) {
      toastError('指定されたファイルは登録できません');
    } else {
      setSelectedFile((_) => file);
      fileToBase64(file, setBase64Content);
      fileInputRef.current!.value = '';
    }
  };

  return (
    <div className="flex-grow-1 my-auto" onPaste={onPaste} onDrop={onDrop}>
      <DraftEditor mentions={mentions} editorState={editorState} setEditorState={setEditorState} />
      <div className="text-end mt-2 d-flex justify-content-end">
        <input
          type="file"
          onChange={onFileInputChange}
          ref={fileInputRef}
          accept={UPLOADABLE_MIME_TYPES.join(',')}
          style={{ display: 'none' }}
        />
        <button className="btn btn-icon-only bg-gradient-dark me-1" onClick={() => fileInputRef.current!.click()}>
          <i className="fas fa-paperclip"></i>
        </button>
        <button className={`btn btn-sm btn-icon bg-gradient-dark ${!sendable && 'disabled'}`} onClick={onSendClick}>
          <i className="fas fa-paper-plane"></i> 送信
        </button>
      </div>
    </div>
  );
};

// Editorにデータが書き込まれているか
const isEditorStateEmpty = (editorState: EditorState) => {
  const contentBlocks = convertToRaw(editorState.getCurrentContent()).blocks;
  return contentBlocks.every((d) => d.text.trim().length == 0 && d.type == 'unstyled');
};
